import { computed, nextTick, ref } from 'vue';

import { getDayHash } from '@components/composable';
import { propsFactory, pad, isObject, getDateDiff, formatDate, warn } from '@components/utils';

import type { TDatetimeValueRange } from '@components/composable';
import type { IRawDate, TDateLocale } from '@components/utils';
import type { PropType, Ref } from 'vue';

export type TCalendarView = typeof availableViews[number];
export type TCalendarAllowedDates = string[] | ((date: string) => boolean);
export type TCalendarEvents = string[] | ((date: string) => boolean);
export type TCalendarEventsColor = 'default' | 'success' | 'danger' | 'warning' | ((date: string) => boolean);
export interface ICalendarProps {
  title?: string;
  description?: string;
  header?: boolean;
  todayBtn?: boolean;
  defaultYearMonth?: string;
  view: TCalendarView;
  multiple?: boolean;
  range?: boolean;
  firstDayOfWeek: number | string;
  allowedDates?: TCalendarAllowedDates;
  minYearMonth?: string;
  maxYearMonth?: string;
  noUnset?: boolean;
  events?: TCalendarEvents;
  eventsColor?: TCalendarEventsColor;
  emitImmediately?: boolean;
  hideSiblingMonth?: boolean;
}

export const YEARS_INTERVAL = 14;
export const lineStr = ' \u2014 ';
export const availableViews = ['Calendar', 'Years', 'Months'] as const;

const yearMonthValidator = (value: string) => /^-?[\d]+\/[0-1]\d$/.test(value);

export const makeCalendarProps = propsFactory(
  {
    /**
     * Заголовок календаря. Отображается в шапке календаря.
     */
    title: {
      type: String as PropType<string>,
      default: undefined
    },
    /**
     * Описание календаря. Отображается в шапке календаря.
     */
    description: {
      type: String as PropType<string>,
      default: undefined
    },
    /**
     * Отображать шапку календаря
     */
    header: {
      type: Boolean as PropType<boolean>,
      default: false
    },
    /**
     * Отображать кнопку "Сегодня" в шапке календаря
     */
    todayBtn: {
      type: Boolean as PropType<boolean>,
      default: false
    },
    /**
     * Отображаемый год/месяц в формате YYYY/MM. По-умолчанию это текущий год/месяц если не указана v-model.
     */
    defaultYearMonth: {
      type: [String] as PropType<string>,
      default: undefined,
      validator: yearMonthValidator
    },
    /**
     * Вид календаря по-умолчанию. Возможные значения: 'Calendar', 'Year', 'Month'.
     */
    view: {
      type: String as PropType<TCalendarView>,
      default: 'Calendar',
      validator: (value: TCalendarView) => availableViews.includes(value)
    },
    /**
     * Разрешает множественный выбор дат. v-model должна быть массивом.
     */
    multiple: {
      type: Boolean as PropType<boolean>,
      default: false
    },
    /**
     * Разрешает выбор диапазона дат. v-model должна быть массивом.
     */
    range: {
      type: Boolean as PropType<boolean>,
      default: false
    },
    /**
     * Первый день недели (понедельник по-умолчанию).
     */
    firstDayOfWeek: {
      type: [String, Number] as PropType<string | number>,
      default: 1
    },
    /**
     * Массив дат, которые будут отображаться в календаре.
     * При использовании функции, функция поучает дату в формате YYYY/MM/DD и должна вернуть true или false.
     */
    allowedDates: {
      type: [Array, Function] as PropType<TCalendarAllowedDates>
    },
    /**
     * Запретить навигацию в календаре до указанной минимальной даты в формате YYYY/MM.
     */
    minYearMonth: {
      type: String as PropType<string>,
      default: undefined,
      validator: yearMonthValidator
    },
    /**
     * Запретить навигацию в календаре после указанной максимальной даты в формате YYYY/MM.
     */
    maxYearMonth: {
      type: String as PropType<string>,
      default: undefined,
      validator: yearMonthValidator
    },
    /**
     * Запретить сброс даты в календаре.
     */
    noUnset: {
      type: Boolean as PropType<boolean>,
      default: false
    },
    /**
     * Список событий, которые будут отображаться в календаре. Формат: YYYY/MM/DD.
     */
    events: {
      type: [Array, Function] as PropType<TCalendarEvents>
    },
    /**
     * Цвет событий в календаре.
     */
    eventsColor: {
      type: String as PropType<TCalendarEventsColor>,
      default: 'default'
    },
    /**
     * Эмитировать событие update:modelValue при смене месяца и года сразу.
     * Недоступно при multiple и range.
     */
    emitImmediately: {
      type: Boolean as PropType<boolean>,
      default: false
    },
    /**
     * Скрыть дни соседних месяцев в календаре.
     */
    hideSiblingMonth: {
      type: Boolean as PropType<boolean>,
      default: false
    }
  },
  'calendar'
);

export function useCalendar(
  props: ICalendarProps,
  model: Ref<readonly (string | TDatetimeValueRange)[]> & {
    readonly externalValue: string | TDatetimeValueRange | (string | TDatetimeValueRange)[];
  },
  internalLocale: Ref<TDateLocale>,
  internalMask: Ref<string>,
  getCurrentDate: () => IRawDate,
  decodeString: (date: string) => IRawDate,
  emitValue: (value: any, action: string, date: IRawDate | TDatetimeValueRange) => void,
  emitImmediately: (reason: string) => void
) {
  const today = computed(() => getCurrentDate());
  const editRange = ref<{ init: IRawDate | null; initHash: number; final: IRawDate | null; finalHash: number } | null>(
    null
  );
  const getNativeDateFn = computed(
    () => (model: IRawDate) => new Date(model.year as number, (model.month as number) - 1, model.day)
  );
  const defaultView = ref(props.view);
  const monthDirection = ref<'left' | 'right'>('left');
  const yearDirection = ref<'left' | 'right'>('left');
  const isImmediate = computed(() => props.emitImmediately && !props.multiple && !props.range);

  const viewModel = ref(getViewModel());
  const daysModel = computed(() =>
    model.value
      .filter((date) => typeof date === 'string')
      .map((date) => decodeString(date as string))
      .filter((date) => date.dateHash && date.day && date.month && date.year)
  );
  const rangeModel = computed(() => {
    const fn = (date: string) => decodeString(date);
    return model.value
      .filter((date) => isObject(date) && (date as TDatetimeValueRange).from && (date as TDatetimeValueRange).to)
      .map((range) => ({
        from: fn((range as TDatetimeValueRange).from as string),
        to: fn((range as TDatetimeValueRange).to as string)
      }))
      .filter((range) => range.from.dateHash && range.to.dateHash && range.from.dateHash < range.to.dateHash);
  });
  const minSelectedModel = computed(() => {
    const model = daysModel.value
      .concat(rangeModel.value.map((range) => range.from))
      .sort((a: IRawDate | any, b: IRawDate | any) => a.year - b.year || a.month - b.month);
    return model[0];
  });
  const maxSelectedModel = computed(() => {
    const model = daysModel.value
      .concat(rangeModel.value.map((range) => range.to))
      .sort((a: IRawDate | any, b: IRawDate | any) => b.year - a.year || b.month - a.month);
    return model[0];
  });
  const daysInModel = computed(
    () =>
      daysModel.value.length +
      rangeModel.value.reduce(
        (acc, range) => acc + 1 + getDateDiff(getNativeDateFn.value(range.to), getNativeDateFn.value(range.from)),
        0
      )
  );
  const viewMonthHash = computed(() => getMonthHash(viewModel.value));

  const year = viewModel.value.year;
  const startYear = ref(year && year - (year % YEARS_INTERVAL) - (year < 0 ? YEARS_INTERVAL : 0));
  const internalFirstDayOfWeek = computed(() =>
    props.firstDayOfWeek ? +props.firstDayOfWeek : internalLocale.value.firstDayOfWeek
  );
  const daysOfWeek = computed(() => {
    const days = internalLocale.value.daysShort;
    const first = internalFirstDayOfWeek.value;

    return first > 0 ? days.slice(first, 7).concat(days.slice(0, first)) : days;
  });
  const daysInMonth = computed(() => {
    const date = viewModel.value;
    return new Date(date.year as number, date.month as number, 0).getDate();
  });

  const minNav = computed(() => {
    if (!props.minYearMonth) return;
    const [year, month] = props.minYearMonth.split('/');

    return {
      year: parseInt(year, 10),
      month: parseInt(month, 10)
    };
  });
  const maxNav = computed(() => {
    if (!props.maxYearMonth) return;
    const [year, month] = props.maxYearMonth.split('/');

    return {
      year: parseInt(year, 10),
      month: parseInt(month, 10)
    };
  });
  const navBoundaries = computed(() => {
    const data = {
      month: { prev: true, next: true },
      year: { prev: true, next: true }
    };

    if (minNav.value && minNav.value.year >= (viewModel.value.year as number)) {
      data.year.prev = false;

      if (minNav.value.year === viewModel.value.year && minNav.value.month >= (viewModel.value.month as number)) {
        data.month.prev = false;
      }
    }

    if (maxNav.value && maxNav.value.year <= (viewModel.value.year as number)) {
      data.year.next = false;

      if (maxNav.value.year === viewModel.value.year && maxNav.value.month <= (viewModel.value.month as number)) {
        data.month.next = false;
      }
    }

    return data;
  });

  const daysMap = computed(() => {
    const map = {} as Record<string, number[]>;

    daysModel.value.forEach((entry) => {
      const hash = getMonthHash(entry);

      if (!map[hash]) {
        map[hash] = [];
      }

      map[hash].push(entry.day as number);
    });

    return map;
  });
  const rangeMap = computed(() => {
    const map = {} as Record<string, Array<{ from?: number; to?: number; range: { from: IRawDate; to: IRawDate } }>>;

    rangeModel.value.forEach((entry) => {
      const hashFrom = getMonthHash(entry.from);
      const hashTo = getMonthHash(entry.to);

      if (!map[hashFrom]) {
        map[hashFrom] = [];
      }

      map[hashFrom].push({
        from: entry.from.day,
        to: hashFrom === hashTo ? entry.to.day : undefined,
        range: entry
      });

      if (hashFrom < hashTo) {
        let hash;
        const { year, month } = entry.from;
        const current =
          (month as number) < 12 ? { year, month: (month as number) + 1 } : { year: (year as number) + 1, month: 1 };

        while ((hash = getMonthHash(current)) <= hashTo) {
          if (!map[hash]) {
            map[hash] = [];
          }

          map[hash].push({
            from: undefined,
            to: hash === hashTo ? entry.to.day : undefined,
            range: entry
          });

          current.month++;

          if (current.month > 12) {
            (current.year as number)++;
            current.month = 1;
          }
        }
      }
    });

    return map;
  });
  const rangeView = computed(() => {
    if (!editRange.value) return;

    const { init, initHash, final, finalHash } = editRange.value;
    const [from, to] = initHash <= finalHash ? [init, final] : [final, init];

    if (!from || !to) return;
    const fromHash = getMonthHash(from);
    const toHash = getMonthHash(to);

    if (fromHash !== viewMonthHash.value && toHash !== viewMonthHash.value) return;

    const view = {} as { from?: number; includeFrom: boolean; to?: number; includeTo: boolean };

    if (fromHash === viewMonthHash.value) {
      view.from = from.day;
      view.includeFrom = true;
    } else {
      view.from = 1;
    }

    if (toHash === viewMonthHash.value) {
      view.to = to.day;
      view.includeTo = true;
    } else {
      view.to = daysInMonth.value;
    }

    return view;
  });
  const selectionDaysMap = computed(() => {
    const map = {} as Record<string, boolean>;

    if (!props.allowedDates) {
      for (let i = 1; i <= daysInMonth.value; i++) {
        map[i] = true;
      }
      return map;
    }

    const fn =
      typeof props.allowedDates === 'function'
        ? props.allowedDates
        : (date: string) => (props.allowedDates as string[])?.includes(date);

    for (let i = 1; i <= daysInMonth.value; i++) {
      const dayHash = viewMonthHash.value + '/' + pad(i);
      map[i] = fn(dayHash);
    }

    return map;
  });
  const evtColor = computed(() =>
    typeof props.eventsColor === 'function' ? props.eventsColor : () => props.eventsColor
  );
  const eventDaysMap = computed(() => {
    const map = {} as Record<string, TCalendarEventsColor | boolean | undefined>;

    if (!props.events) {
      for (let i = 1; i <= daysInMonth.value; i++) {
        map[i] = false;
      }
    } else {
      const fn =
        typeof props.events === 'function'
          ? props.events
          : (date: string) => (props.events as string[])?.includes(date);
      for (let i = 0; i <= daysInMonth.value; i++) {
        const dayHash = viewMonthHash.value + '/' + pad(i);
        map[i] = fn(dayHash) && evtColor.value(dayHash);
      }
    }

    return map;
  });

  const viewDays = computed(() => {
    const { year, month } = viewModel.value;
    const date = new Date(year as number, (month as number) - 1, 1);
    const endDay = new Date(year as number, (month as number) - 1, 0).getDate();

    return {
      days: date.getDay() - internalFirstDayOfWeek.value - 1,
      endDay
    };
  });

  const days = computed(() => {
    type TDaysResult = {
      i: number;
      fill?: boolean;
      event?: boolean | TCalendarEventsColor;
      classes?: string[];
      in?: boolean;
      flat?: boolean;
      selected?: boolean;
      unelevated?: boolean;
      range?: { from: IRawDate; to: IRawDate };
      rangeFrom?: boolean;
      rangeTo?: boolean;
      editRange?: boolean;
      editRangeFrom?: boolean;
      editRangeTo?: boolean;
      today?: boolean;
      color?: string;
    };

    const result = [] as TDaysResult[];
    const { days, endDay } = viewDays.value;

    const len = days < 0 ? days + 7 : days;
    if (len < 6) {
      for (let i = endDay - len; i <= endDay; i++) {
        result.push({ i, fill: true });
      }
    }

    const index = result.length;

    for (let i = 1; i <= daysInMonth.value; i++) {
      const day: TDaysResult = {
        i,
        event: eventDaysMap.value[i],
        classes: []
      };

      if (selectionDaysMap.value[i]) {
        day.in = true;
        day.flat = true;
      }

      result.push(day);
    }

    if (daysMap.value[viewMonthHash.value]) {
      daysMap.value[viewMonthHash.value].forEach((day) => {
        const i = index + day - 1;
        result[i] = {
          ...result[i],
          selected: true,
          unelevated: true,
          flat: false
        };
      });
    }

    if (rangeMap.value[viewMonthHash.value]) {
      rangeMap.value[viewMonthHash.value].forEach((entry) => {
        if (entry.from) {
          const from = index + entry.from - 1;
          const to = index + (entry.to || daysInMonth.value) - 1;

          for (let day = from; day <= to; day++) {
            result[day] = {
              ...result[day],
              range: entry.range,
              unelevated: true
            };
          }

          result[from] = {
            ...result[from],
            rangeFrom: true,
            flat: false
          };

          if (entry.to) {
            result[to] = {
              ...result[to],
              rangeTo: true,
              flat: false
            };
          }
        } else if (entry.to) {
          const to = index + entry.to - 1;

          for (let day = index; day <= to; day++) {
            result[day] = {
              ...result[day],
              range: entry.range,
              unelevated: true
            };
          }

          result[to] = {
            ...result[to],
            rangeTo: true,
            flat: false
          };
        } else {
          const to = index + daysInMonth.value - 1;

          for (let day = index; day <= to; day++) {
            result[day] = {
              ...result[day],
              range: entry.range,
              unelevated: true
            };
          }
        }
      });
    }

    if (rangeView.value && rangeView.value?.from && rangeView.value?.to) {
      const from = index + rangeView.value?.from - 1;
      const to = index + rangeView.value?.to - 1;

      for (let day = from; day <= to; day++) {
        result[day].editRange = true;
      }

      if (rangeView.value?.includeFrom) {
        result[from].editRangeFrom = true;
      }

      if (rangeView.value?.includeTo) {
        result[to].editRangeTo = true;
      }
    }

    if (viewModel.value.year === today.value.year && viewModel.value.month === today.value.month) {
      result[index + (today.value.day as number) - 1].today = true;
    }

    const left = result.length % 7;
    if (left > 0) {
      const afterDays = 7 - left;
      for (let i = 1; i <= afterDays; i++) {
        result.push({ i, fill: true });
      }
    }

    result.forEach((day) => {
      const classes = ['cds-date-picker__calendar-item'];

      if (day.fill) {
        classes.push('cds-date-picker__calendar-item--fill');

        if (props.hideSiblingMonth) {
          classes.push('cds-date-picker__calendar-item--hide-sibling-month');
        }
      } else {
        classes.push(`cds-date-picker__calendar-item--${day.in ? 'in' : 'out'}`);

        if (day.range) {
          classes.push(`cds-date-picker__calendar-item__range${day.rangeTo ? '--to' : day.rangeFrom ? '--from' : ''}`);
        }

        if (day.editRange) {
          classes.push(
            `cds-date-picker__calendar-item__edit-range${day.editRangeFrom ? '--from' : ''}${
              day.editRangeTo ? '--to' : ''
            }`
          );
        }
      }

      day.classes = classes;
    });

    return result;
  });

  const encodeObjectFn = computed(
    () => (date: IRawDate, mask?: string, locale?: TDateLocale) =>
      formatDate(
        new Date(
          date.year as number,
          (date.month as number) - 1,
          date.day,
          date.hour,
          date.minute,
          date.second,
          date.millisecond
        ),
        mask ? mask : internalMask.value,
        locale ? locale : internalLocale.value,
        date.year as number,
        date.timezoneOffset as number
      )
  );

  function getViewModel() {
    if (!model.value.length) {
      return getDefaultViewModel();
    }

    const target = model.value[model.value.length - 1];
    const decoded = decodeString(
      (target as TDatetimeValueRange).from ? ((target as TDatetimeValueRange).from as string) : (target as string)
    );

    return decoded.dateHash === null ? getDefaultViewModel() : decoded;
  }

  function getDefaultViewModel(): IRawDate {
    let year, month;

    if (props.defaultYearMonth) {
      [year, month] = props.defaultYearMonth.split('/');
      year = parseInt(year, 10);
      month = parseInt(month, 10);
    } else {
      const now = today.value ? today.value : getCurrentDate();

      year = now.year;
      month = now.month;
    }

    return {
      year,
      month,
      day: 1,
      hour: 0,
      minute: 0,
      second: 0,
      millisecond: 0,
      dateHash: year + '/' + pad(month as number) + '/01'
    };
  }

  function updateViewModel(year: number, month: number, time?: IRawDate) {
    if (minNav.value && year <= minNav.value.year) {
      year = minNav.value.year;

      if (month < minNav.value.month) {
        month = minNav.value.month;
      }
    }

    if (maxNav.value && year >= maxNav.value.year) {
      year = maxNav.value.year;

      if (month > maxNav.value.month) {
        month = maxNav.value.month;
      }
    }

    if (time) {
      const { hour, minute, second, millisecond, timezoneOffset, timeHash } = time;
      viewModel.value = {
        ...viewModel.value,
        hour,
        minute,
        second,
        millisecond,
        timezoneOffset,
        timeHash
      };
    }

    const newHash = year + '/' + pad(month) + '/01';

    if (newHash !== viewModel.value.dateHash) {
      monthDirection.value = viewModel.value.dateHash && viewModel.value.dateHash < newHash ? 'left' : 'right';
      if (year !== viewModel.value.year) {
        yearDirection.value = monthDirection.value;
      }

      nextTick(() => {
        startYear.value = year - (year % YEARS_INTERVAL) - (year < 0 ? YEARS_INTERVAL : 0);
        viewModel.value = {
          ...viewModel.value,
          year,
          month,
          day: 1,
          dateHash: newHash
        };
      });
    }
  }

  function setCalendarTo(year: number, month: number) {
    const [calendar] = availableViews;
    defaultView.value = calendar;
    updateViewModel(year, month);
  }
  function setToday() {
    const date = today.value;
    const month = daysMap.value[getMonthHash(date)];

    if (!month || !month.includes(date.day as number)) {
      addToModel(date);
    }

    setCalendarTo(date.year as number, date.month as number);
  }

  function addToModel(date: IRawDate) {
    let value;

    if (props.multiple) {
      const internalModel = model.value.slice();
      internalModel.push(encodeEntry(date) as string);
      value = internalModel;
    } else {
      value = encodeEntry(date);
    }

    emitValue(value, 'add', date);
  }

  function addToModelRange(date: TDatetimeValueRange) {
    let value;

    if (props.multiple) {
      if (date.from) {
        const fromHash = getDayHash(date.from as IRawDate);
        const toHash = getDayHash(date.to as IRawDate);
        const days = daysModel.value.filter((day) =>
          day.dateHash ? day.dateHash < fromHash || day.dateHash > toHash : false
        );
        const ranges = rangeModel.value.filter(({ from, to }) =>
          to.dateHash && from.dateHash ? to.dateHash < fromHash || from.dateHash > toHash : false
        );

        value = [...days, ...ranges, date].map((entry) => encodeEntry(entry));
      }
    } else {
      value = encodeEntry(date);
    }

    emitValue(value, 'add', date);
  }

  function removeFromModel(date: IRawDate) {
    if (props.noUnset) return;

    let value = null;

    if (props.multiple) {
      const val = encodeEntry(date);
      value = model.value.filter((date) => date !== val);

      if (!value.length) {
        value = null;
      }
    }

    emitValue(value, 'remove', date);
  }
  function removeFromModelRange(date: TDatetimeValueRange) {
    if (props.noUnset) return;

    let value = null;

    if (props.multiple) {
      const val = encodeEntry(date);

      if (date.from) {
        value = model.value.filter((date) => {
          (date as TDatetimeValueRange).from
            ? (date as TDatetimeValueRange).from !== (val as TDatetimeValueRange).from &&
              (date as TDatetimeValueRange).to !== (val as TDatetimeValueRange).to
            : true;
        });

        if (!value.length) {
          value = null;
        }
      }
    }

    emitValue(value, 'remove', date);
  }

  function encodeEntry(date: any, mask?: string, locale?: TDateLocale) {
    return date.from
      ? { from: encodeObjectFn.value(date.from, mask, locale), to: encodeObjectFn.value(date.to, mask, locale) }
      : encodeObjectFn.value(date, mask, locale);
  }

  function goToMonth(offset: number) {
    let year = viewModel.value.year as number;
    let month = +(viewModel.value.month as number) + offset;

    if (month === 13) {
      month = 1;
      year++;
    } else if (month === 0) {
      month = 12;
      year--;
    }

    updateViewModel(year, month);
    isImmediate.value && emitImmediately('month');
  }

  function goToYear(offset: number) {
    const year = +(viewModel.value.year as number) + offset;
    updateViewModel(year, viewModel.value.month as number);
    isImmediate.value && emitImmediately('year');
  }

  function setYear(year: number) {
    updateViewModel(year, viewModel.value.month as number);
    defaultView.value = props.view === 'Years' ? 'Months' : 'Calendar';
    isImmediate.value && emitImmediately('year');
  }

  function setMonth(month: number) {
    updateViewModel(viewModel.value.year as number, month);
    defaultView.value = 'Calendar';
    isImmediate.value && emitImmediately('month');
  }

  function setView(viewMode: TCalendarView) {
    if (!availableViews.includes(viewMode)) {
      warn('calendar', `Calendar doesn't support ${viewMode} view`);
      return;
    }

    defaultView.value = viewMode;
  }

  function toggleDate(date: IRawDate | TDatetimeValueRange, monthHash: string) {
    const month = daysMap.value[monthHash];

    if ((date as TDatetimeValueRange).from) {
      const fn = month && month.includes((date as IRawDate).day as number) ? removeFromModelRange : addToModelRange;
      fn(date as TDatetimeValueRange);
    } else {
      const fn = month && month.includes((date as IRawDate).day as number) ? removeFromModel : addToModel;
      fn(date as IRawDate);
    }
  }

  function offsetCalendar(type: 'month' | 'year', descending: number) {
    if (['month', 'years'].includes(type)) {
      const fn = type === 'month' ? goToMonth : goToYear;
      fn(descending ? -1 : 1);
    }
  }

  function setEditingRange(from: IRawDate, to: IRawDate) {
    if (!props.range || !from) {
      editRange.value = null;
      return;
    }

    const init = { ...viewModel.value, from };
    const final = to ? { ...viewModel.value, to } : init;

    editRange.value = {
      init,
      initHash: getDayHash(init) as unknown as number,
      final,
      finalHash: getDayHash(final) as unknown as number
    };

    setCalendarTo(init.year as number, init.month as number);
  }

  return {
    today,
    editRange,
    getNativeDateFn,
    defaultView,
    monthDirection,
    yearDirection,
    isImmediate,

    viewModel,
    daysModel,
    rangeModel,
    minSelectedModel,
    maxSelectedModel,
    daysInModel,

    startYear,
    daysOfWeek,
    daysInMonth,
    viewMonthHash,

    minNav,
    maxNav,
    navBoundaries,

    days,
    daysMap,
    rangeMap,
    rangeView,
    selectionDaysMap,

    getViewModel,
    updateViewModel,
    setToday,
    setCalendarTo,
    goToMonth,
    goToYear,
    setMonth,
    setYear,
    setView,
    setEditingRange,
    toggleDate,
    addToModel,
    addToModelRange,
    removeFromModel,
    removeFromModelRange,
    offsetCalendar,
    encodeEntry
  };
}

export function getMonthHash(date: IRawDate) {
  return date.year + '/' + pad(date.month as number);
}
